/**
 * i18n Configuration
 *
 * React-i18next setup for ERP internationalization
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  type Locale,
  DEFAULT_LOCALE,
  type TranslationNamespace,
} from './types';
import { getStoredLocale, storeLocale } from './storage';

/**
 * i18n initialization configuration
 *
 * Captured promise resolves once i18next has initialized AND finished loading
 * every namespace declared in `ns` for the active language. Awaiting it before
 * the React app mounts guarantees the first render sees fully-loaded translations,
 * which prevents raw translation keys (e.g. `shipment.reserve`) from leaking into
 * the DOM when a component first-paints before its namespace finishes async-loading
 * (we run with `react.useSuspense: false`, and memoized table cells such as antd
 * rc-table rows would otherwise cache the unresolved key).
 */
export const i18nInitialized: Promise<unknown> = i18n
  // Detect user language
  .use(LanguageDetector)
  // Load translations using HTTP backend
  .use(HttpBackend)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Default language
    lng: getStoredLocale() || DEFAULT_LOCALE,

    // Fallback language when translation is missing
    fallbackLng: 'zh-CN',

    // Supported languages
    supportedLngs: ['zh-CN', 'en-US'],

    // Default namespace
    defaultNS: 'common',

    // Namespaces to load
    ns: [
      'common',
      'nav',
      'validation',
      'errors',
      'masterData',
      'inventory',
      'sales',
      'procurement',
      'production',
      'finance',
      'reports',
      'documents',
      'admin',
    ] as TranslationNamespace[],

    // Backend configuration for loading translation files
    backend: {
      // Path to translation files
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Language detection configuration
    detection: {
      // Order of language detection
      order: ['localStorage', 'navigator'],

      // Keys for storing/reading language preference
      lookupLocalStorage: 'erp-locale',

      // Cache user language in localStorage
      caches: ['localStorage'],
    },

    interpolation: {
      // React already escapes values
      escapeValue: false,

      // Interpolation format
      formatSeparator: ',',
    },

    // React configuration
    react: {
      // Wait for all translations before rendering
      useSuspense: false,
    },

    // Development options
    debug: import.meta.env.DEV,

    // Missing key handling
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lng, ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`Missing translation key: ${ns}.${key} (${lng})`);
      }
    },

    // Missing translation handling
    returnEmptyString: false,
    returnNull: false,
  });

/**
 * Listen for language changes and persist to localStorage
 */
i18n.on('languageChanged', (lng: string) => {
  storeLocale(lng as Locale);
});

/**
 * Change the current language
 */
export function changeLanguage(locale: Locale): Promise<void> {
  return new Promise((resolve, reject) => {
    i18n.changeLanguage(locale, (err) => {
      if (err) {
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

/**
 * Get the current language
 */
export function getCurrentLanguage(): Locale {
  return (i18n.language || DEFAULT_LOCALE) as Locale;
}

/**
 * Check if i18n is initialized
 */
export function isI18nInitialized(): boolean {
  return i18n.isInitialized;
}

export default i18n;
